import { environment } from './environment';

/**
 * Reads a deterministic arm/client override for local dev and E2E — NEVER
 * honored in production, and NEVER by writing Remote Config. The shared
 * `vaquinha-dev` Remote Config template stays untouched by this: it's a
 * purely client-side, per-tab convenience so a Remote-Config-gated rollout
 * (e.g. `create_vaki_flow_variant_rollout_percentage`) or a data-access
 * client selection (e.g. `provideLolaCreateVakiClient`) can be forced from a
 * URL in local dev or an E2E run — see qa-tester's Remote Config
 * write-permission block on PR #2230's re-verify (Notion
 * 3e5c94b6-2cc7-81b9-816a-f6b1af37ad34): no session is allowed to write that
 * shared template just to exercise an arm.
 *
 * Reads `paramName` off the current URL's query string once and persists it
 * to `sessionStorage` under the same key, so the override survives
 * client-side navigation within the SAME TAB (e.g. `/crear` redirecting
 * through a login round trip and back) without needing the query param on
 * every URL. A later call with no query param but an existing
 * `sessionStorage` entry keeps using it; there's no "clear the override"
 * value — passing a different member of `allowedValues` on the URL replaces
 * it, and a fresh tab starts with none.
 *
 * Browser-only: resolves `null` under SSR (`window` undefined), exactly like
 * every other arm/variant resolver in this codebase falls back to
 * control/false on the server. No request-based reading here on purpose —
 * that's a separate, larger change (tracked against the CI issue that needs
 * an SSR-visible override for its route smoke, Notion
 * 3e7c94b6-2cc7-81fa-8098-c2c8849f7d00); this stays small and generic for
 * its two current callers, both of which only ever run this in the browser.
 */
export function resolveNonProdOverride<T extends string>(
  paramName: string,
  allowedValues: readonly T[]
): T | null {
  if (environment.production) return null;
  if (typeof window === 'undefined') return null;

  const isAllowed = (value: string | null): value is T =>
    !!value && (allowedValues as readonly string[]).includes(value);

  try {
    const fromQuery = new URLSearchParams(window.location.search).get(
      paramName
    );
    if (isAllowed(fromQuery)) {
      window.sessionStorage.setItem(paramName, fromQuery);
      return fromQuery;
    }

    const stored = window.sessionStorage.getItem(paramName);
    if (isAllowed(stored)) return stored;
  } catch {
    // sessionStorage can throw (private browsing, disabled storage, a
    // sandboxed iframe) — fail safe to "no override", never to a thrown
    // error breaking /crear or client selection.
  }

  return null;
}
